import { useMemo, useState } from 'react'
import { integrationsConfig } from '../../config'
import { GhostButton } from '../shared/GhostButton'
import { formatTimestamp } from '../../utils/formatters'

interface ProviderCardProps {
  name: string
  scope: string
  status: 'connected' | 'pending' | 'available'
  lastSynced?: string
}

type ProviderStatus = ProviderCardProps['status']

interface ProviderState {
  status: ProviderStatus
  lastSynced?: string
}

function ProviderCard({ name, scope, status, lastSynced }: ProviderCardProps) {
  const statusCopy: Record<ProviderCardProps['status'], string> = {
    connected: 'Connected',
    pending: 'Pending',
    available: 'Available'
  }

  const statusClass =
    status === 'connected'
      ? 'text-brand-glow border-brand-glow/40 bg-brand-glow/10'
      : status === 'pending'
        ? 'text-brand-alert border-brand-alert/40 bg-brand-alert/10'
        : 'text-muted border-border-light bg-surface-light'

  return (
    <div className="rounded-xl border border-border-light bg-surface-light p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-ink">{name}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted">{scope}</div>
        </div>
        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] ${statusClass}`}>
          {statusCopy[status]}
        </span>
      </div>
      {lastSynced && (
        <div className="mt-3 text-xs text-muted">Last sync {lastSynced}</div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <GhostButton>{status === 'connected' ? 'Manage' : 'Connect'}</GhostButton>
        {status !== 'connected' && <span className="text-[11px] text-muted">OAuth • Read-only</span>}
      </div>
    </div>
  )
}

export function IntegrationsPanel() {
  const [showMore, setShowMore] = useState(false)
  const { primary, more, hard_kill } = integrationsConfig

  const connectionState = useMemo<Record<string, ProviderState>>(
    () => ({
      stripe: { status: 'connected', lastSynced: formatTimestamp() },
      ga4: { status: 'pending' },
      posthog: { status: 'available' }
    }),
    []
  )

  const totalSources = primary.length
  const connectedSources = primary.filter((provider) => connectionState[provider.id as keyof typeof connectionState]?.status === 'connected').length
  const completion = Math.round((connectedSources / totalSources) * 100)

  return (
    <section id="integrations" className="col-span-1 md:col-span-3 lg:col-span-2">
      <div className="rounded-2xl border border-border bg-surface p-6 shadow-[0_12px_50px_rgba(8,13,24,0.6)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-muted">Connect data sources</p>
            <h3 className="mt-1 text-xl font-semibold text-ink">Stripe · PostHog · GA4</h3>
            <p className="text-sm text-muted">Read-only ingest with API hard-kills for {hard_kill.join(', ')}.</p>
          </div>
          <span className="rounded-md border border-border-light bg-surface-light px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
            OAuth
          </span>
        </div>
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{connectedSources} / {totalSources} connected</span>
            <span>{completion}% complete</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-light">
            <div className="h-full rounded-full bg-brand-glow" style={{ width: `${completion}%` }} />
          </div>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {primary.map((provider) => {
            const state: ProviderState = connectionState[provider.id] ?? { status: 'available' as ProviderStatus }
            return (
              <ProviderCard
                key={provider.id}
                name={provider.label}
                scope={provider.scope}
                status={state.status}
                lastSynced={state.lastSynced}
              />
            )
          })}
        </div>
        <div className="mt-6 text-sm">
          <button
            type="button"
            onClick={() => setShowMore((prev) => !prev)}
            className="text-brand-glow underline-offset-4 hover:text-ink"
          >
            {showMore ? 'Hide additional integrations' : 'Show additional integrations'}
          </button>
        </div>
        {showMore && (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {more.map((provider) => (
              <ProviderCard
                key={provider.id}
                name={provider.label}
                scope={provider.scope}
                status="available"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
